import type { RoleMarketSummary } from './role-market-summary';
import type { OfficialEarnings } from './official-earnings';

/**
 * Derived, page-ready findings for the role salary page. Pure functions over data the loader
 * already fetched, so every sentence the page prints is traceable to a number and the gates
 * (minimum samples, minimum gaps) live in one tested place.
 */

/** A sector gap smaller than this is "about the same", not a finding. */
export const MIN_SECTOR_GAP_EUR = 100;
/** Ads vs official earnings gap smaller than this isn't worth a callout. */
export const MIN_OFFICIAL_GAP_EUR = 150;
/** A language requirement is worth a callout from this share of open ads. */
export const MIN_LANGUAGE_SHARE_PCT = 10;

export interface SectorGap {
  publicPay: number;
  privatePay: number;
  /** public - private, rounded to 10 €. Positive: public pays more. */
  gap: number;
  publicCount: number;
  privateCount: number;
}

export function computeSectorGap(summary: RoleMarketSummary | null | undefined): SectorGap | null {
  if (!summary) return null;
  const pub = summary.sectors.find((s) => s.sector === 'public');
  const priv = summary.sectors.find((s) => s.sector === 'private');
  if (!pub?.median || !priv?.median) return null;
  const gap = Math.round((pub.median - priv.median) / 10) * 10;
  if (Math.abs(gap) < MIN_SECTOR_GAP_EUR) return null;
  return { publicPay: pub.median, privatePay: priv.median, gap, publicCount: pub.statedCount, privateCount: priv.statedCount };
}

export interface OfficialGap {
  official: number;
  advertised: number;
  /** official - advertised, rounded to 10 €. */
  gap: number;
  year: number;
}

export function computeOfficialGap(official: OfficialEarnings | null | undefined, advertisedMedian: number | null, year: number): OfficialGap | null {
  const o = official?.total?.regularMedian;
  if (!o || !advertisedMedian) return null;
  const gap = Math.round((o - advertisedMedian) / 10) * 10;
  if (Math.abs(gap) < MIN_OFFICIAL_GAP_EUR) return null;
  return { official: o, advertised: Math.round(advertisedMedian), gap, year };
}

/** Official sector gap (wellbeing areas vs private), when both are published. */
export function computeOfficialSectorGap(official: OfficialEarnings | null | undefined): { wellbeing: number; privateSector: number; gap: number } | null {
  const w = official?.wellbeingAreas?.regularMedian;
  const p = official?.privateSector?.regularMedian;
  if (!w || !p) return null;
  return { wellbeing: w, privateSector: p, gap: Math.round((w - p) / 10) * 10 };
}

const LANGUAGE_KEYS: Record<string, 'fi' | 'sv' | 'en'> = {
  finnish: 'fi', suomi: 'fi', finska: 'fi',
  swedish: 'sv', ruotsi: 'sv', svenska: 'sv',
  english: 'en', englanti: 'en', engelska: 'en',
};

export interface LanguageShare {
  lang: 'fi' | 'sv' | 'en';
  label: string;
  pct: number;
  count: number;
}

/** Share of open ads requiring each of Finnish, Swedish and English, from the localized facet. */
export function computeLanguageShares(facet: Array<{ value: string; count: number }> | undefined, openCount: number): LanguageShare[] {
  if (!facet || openCount <= 0) return [];
  const byLang = new Map<'fi' | 'sv' | 'en', LanguageShare>();
  for (const { value, count } of facet) {
    const lang = LANGUAGE_KEYS[value.trim().toLowerCase()];
    if (!lang) continue;
    const prev = byLang.get(lang);
    const total = (prev?.count ?? 0) + count;
    byLang.set(lang, { lang, label: prev?.label ?? value, count: total, pct: Math.min(100, Math.round((total / openCount) * 100)) });
  }
  return (['fi', 'sv', 'en'] as const).map((l) => byLang.get(l)).filter((x): x is LanguageShare => Boolean(x));
}

/**
 * Employer names for running text: drop the legal-form suffix and the long tail of HUS's
 * official name ("Attendo Oy" -> "Attendo", "HUS Helsingin yliopistollinen sairaala" -> "HUS").
 * Display only; links keep the full name.
 */
export function shortEmployerName(name: string): string {
  const n = name.trim();
  if (/^hus\b/i.test(n)) return 'HUS';
  return n.replace(/\s+(oyj|oy|ab|ltd|ky|ry|rf|abp)\.?$/i, '');
}

/** Percent of the comparison base, signed ("12" means 12 % above, "-8" 8 % below). */
export function percentVs(value: number, base: number): number {
  return Math.round(((value - base) / base) * 100);
}

/**
 * Where a monthly pay sits among the role's ads, interpolated from the published percentile
 * points. Clamped to 5..95: outside the P10..P90 band the honest answer is "bottom/top tenth",
 * not a precise rank.
 */
export function percentileOf(pay: number, points: Array<[number, number]>): number {
  const pts = points.filter(([v]) => v > 0).sort((a, b) => a[0] - b[0]);
  if (pts.length === 0) return 50;
  if (pay <= pts[0][0]) return Math.max(5, pts[0][1] - 5);
  const last = pts[pts.length - 1];
  if (pay >= last[0]) return Math.min(95, last[1] + 5);
  for (let i = 0; i < pts.length - 1; i++) {
    const [v0, p0] = pts[i];
    const [v1, p1] = pts[i + 1];
    if (pay <= v1) return Math.round(p0 + ((pay - v0) / (v1 - v0)) * (p1 - p0));
  }
  return 50;
}
